import { CHAT_SEARCH_MIN_QUERY_LENGTH } from "@frontend-shared/constants";
import type { IChatListObject } from "@frontend-shared/types";
import { getDateFormat } from "@frontend-shared/utils/date";
import { format } from "date-fns";
import type { RefCallback } from "react";
import { useTranslation } from "react-i18next";

import { Message, SearchEmpty } from "@/components/icons";
import { SpinningLoader } from "@/components/SpinningLoader";
import { Button } from "@/components/ui/button";
import { CommandGroup, CommandItem } from "@/components/ui/command";
import { getAutoDirection } from "@/lib/direction";

import {
  GLOBAL_SEARCH_ROW_CLASS_NAME,
  GLOBAL_SEARCH_ROW_ICON_CLASS_NAME,
} from "./global-search/constants";

interface ISearchResultsProps {
  isLoading: boolean;
  isError: boolean;
  isBelowMinLength: boolean;
  chats: IChatListObject[];
  isFetchingNextPage: boolean;
  onRetry: () => void;
  onSelectChat: (chat: IChatListObject) => void;
  loadMoreRef: RefCallback<HTMLDivElement>;
}

export const SearchResults = ({
  isLoading,
  isError,
  isBelowMinLength,
  chats,
  isFetchingNextPage,
  onRetry,
  onSelectChat,
  loadMoreRef,
}: ISearchResultsProps) => {
  const { t, i18n } = useTranslation("chat");

  // NOTE: NSAI-4334 — checked before the loading state so shortening a query
  // back down to one character swaps straight to the hint. Rendering
  // "no results" here would be a lie: the server never looked.
  if (isBelowMinLength) {
    return (
      <div
        className="h-full min-h-48 flex items-center justify-center flex-col gap-3 text-foreground-secondary"
        data-testid="search-chats-min-length-hint"
      >
        <SearchEmpty className="h-8 w-8" />
        <span className="text-sm text-foreground-secondary">
          {t("search.min_length_hint", {
            minLength: CHAT_SEARCH_MIN_QUERY_LENGTH,
          })}
        </span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="h-full flex items-center justify-center"
        data-testid="search-chats-loading-state"
      >
        <SpinningLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="h-full flex items-center justify-center flex-col gap-4"
        data-testid="search-chats-error"
      >
        <span className="text-sm text-foreground-secondary">
          {t("search.error_loading")}
        </span>
        <Button data-testid="search-chats-retry" onClick={onRetry}>
          {t("search.retry")}
        </Button>
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div
        className="h-full min-h-48 flex items-center justify-center flex-col gap-3 text-foreground-secondary"
        data-testid="search-chats-empty"
      >
        <SearchEmpty className="h-8 w-8" />
        <span className="text-sm">{t("search.no_results")}</span>
      </div>
    );
  }

  return (
    <CommandGroup className="p-0" data-testid="search-chats-items-group">
      {chats.map((chat) => (
        <CommandItem
          key={chat.uuid}
          value={chat.uuid}
          onSelect={() => onSelectChat(chat)}
          className={GLOBAL_SEARCH_ROW_CLASS_NAME}
          data-testid={`search-chats-item-${chat.uuid}`}
        >
          <span className={GLOBAL_SEARCH_ROW_ICON_CLASS_NAME}>
            <Message className="h-[18px] w-[18px]" />
          </span>
          <p
            dir={getAutoDirection({ content: chat.title })}
            className="min-w-0 flex-1 truncate text-start text-sm text-foreground"
          >
            {chat.title}
          </p>
          <p className="shrink-0 text-xs text-foreground-secondary">
            <span className="sr-only">{t("search.chat_created_on")} </span>
            {format(new Date(chat.createdAt), getDateFormat(i18n.language))}
          </p>
        </CommandItem>
      ))}
      {/* NOTE: NSAI-6853 — a 1px sentinel for the infinite scroll; it only
          takes room while the next page is loading, so a capped search list
          does not end in an empty band. */}
      <div
        ref={loadMoreRef}
        className={isFetchingNextPage ? "px-2.5 py-3" : "h-px"}
        data-testid="search-chats-load-more"
      >
        {isFetchingNextPage && (
          <div className="flex items-center justify-center">
            <SpinningLoader />
          </div>
        )}
      </div>
    </CommandGroup>
  );
};
